<template>
    <!--
        Content Desired:
        - Name
        - Alarm Reading
        - Time + Readable Time
        - Status Icon
        - Type
        - Area / Zone
     -->
    <div>
        <slide-over-panel   v-model:show="showModal">
            <template #title>
                <div class="flex flex-col">
                    <span>{{ modalAlarm.name }}
                        <span class="text-sm text-gray-500">- in {{ modalAlarm.zone }}</span>
                    </span>

                    <span class="text-sm text-gray-500 ml-2">{{ modalAlarm.area }}</span>
                </div>
            </template>

            <template #content>
                <ul v-if="modalAlarm" class="m-0">
                    <!-- Alarm Reading & Type -->
                    <li class="grow px-4 py-4 sm:px-6">
                        <div class="px-2 font-semibold bg-gray-100 py-2 my-2">
                            Alarm Reading
                        </div>
                        <div class="px-3 flex flex-col">
                            <span class="font-semibold"> {{ modalAlarm.reading }}</span>
                            <span class="text-gray-500"> {{ modalAlarm.time }}</span>
                        </div>
                    </li>

                    <!-- Status -->
                    <li class="grow px-4 py-4 sm:px-6">
                        <div class="px-2 font-semibold bg-gray-100 py-2 my-2">
                            Alarm Status
                        </div>
                        <div class="px-3 flex flex-col">
                            <span class="font-semibold">
                                {{ alarmStatus(modalAlarm) }}
                            </span>
                            <span class="text-gray-500">{{ alarmTime(modalAlarm) }}</span>
                        </div>
                    </li>

                    <!-- Last Action -->
                    <li class="grow px-4 py-4 sm:px-6">
                        <div class="px-2 font-semibold bg-gray-100 py-2 my-2">
                            Last Action
                        </div>
                        <div class="px-3 flex flex-col">
                            <span class="font-semibold">
                              {{ getLastCommentActionText(modalAlarm) }} by
                              {{ getLastCommentUser(modalAlarm) }}
                            </span>
                            <span class="text-gray-500">{{ modalAlarm.last_comment_time }}</span>
                        </div>
                    </li>

                    <li class="px-4 py-8 sm:px-6 w-full">
                        <a  :href="company.buildCompanyRoute(`alarms/${modalAlarm.id}`)"
                            class="flex justify-center w-full px-4 py-2 border border-transparent text-sm
                                  leading-5 font-medium text-center rounded-md text-white bg-blue-600 hover:bg-blue-500
                                  focus:outline-none focus:ring-blue-200 focus:border-blue-700 active:bg-blue-700
                                  cursor-pointer"
                        >
                            <span>View Alarm</span>
                        </a>
                    </li>
                </ul>
            </template>
        </slide-over-panel>

        <!-- Slide over to mass address alarms using the existing address alarm components. -->
        <slide-over-panel   v-model:show="showMassAddressSideOver"
                            width="max-w-4xl">
            <template #title>
                <div class="flex flex-col">
                    Mass Address Alarms
                </div>
            </template>

            <template #content>
                <div class="flex flex-col">
                    <!-- Address Alarm -->
                    <div    class="">
                        <!-- Render the alarm address, with events only and since this only works on unresolved alarms
                            we don't need to lock comment only.
                        -->
                        <alarm-address  :comment-only="false"
                                        :is-mass-resolving="true"
                                        :send-event-only="true"
                                        :timezone='timezone'
                                        @adding-comment="massAddress('comment', $event)"
                                        @snoozing-alarm="massAddress('snooze', $event)"
                                        @resolving-alarm="massAddress('resolve', $event)"
                        ></alarm-address>
                    </div>

                    <!-- Cancel Button -->
                    <div class="my-2 flex flex-row justify-center">
                        <button-blue-secondary class="flex flex-row items-center justify-center"
                                              @click="showMassAddressSideOver = false"
                        >
                            Cancel
                        </button-blue-secondary>
                    </div>
                </div>
            </template>
        </slide-over-panel>

        <inline-tab-header
            :headers="filterHeaders"
            name="Filter By"
            :current="activeHeaderFilter"
            @update:current="updateCurrentFilter"
        ></inline-tab-header>

        <div class="py-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 flex flex-row">
            <div class="mx-4 mb-2 flex flex-row md:col-span-2 lg:col-span-3 items-center justify-start">

                <!-- Headers Without Page Name-number -->
                <div class="flex flex-col justify-around w-full">
                    <!-- Search -->
                    <div class="flex flex-col justify-between md:flex-row md:items-center">
                        <div class="w-full md:w-1/2">
                            <floating-input
                                label="Search"
                                v-model:value="search"
                            ></floating-input>
                        </div>

                        <!-- Radio for alarm type filters. -->
                        <ul aria-labelledby="radiogroup-label" class="w-full md:w-1/2 grid lg:grid-cols-2 2xl:grid-cols-3 gap-1 justify-around items-center mb-4" role="radiogroup">
                            <li     id="radiogroup-option-0" tabindex="0" role="radio"
                                    class="mx-2 w-full md:w-4/5 group relative bg-white rounded-lg shadow-sm cursor-pointer ring-0 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    @click="showOfflineClicked"
                            >
                                <div class="rounded-lg border border-gray-300 bg-white px-6 py-2 hover:border-gray-400 flex flex-row justify-around items-center">
                                    <span class="relative flex items-center bg-white mr-2 mb-1">
                                        <label for="offline-select-all" class="sr-only">Select all</label>
                                        <input id="offline-select-all" :checked="showOffline" type="checkbox" name="offline-select-all" class="text-blue-600 focus:ring-blue-500 rounded cursor-pointer">
                                    </span>
                                    <div class="text-sm">
                                        <p class="font-medium text-gray-900 hover:text-gray-700">
                                            Offline
                                        </p>
                                    </div>
                                </div>
                                <!-- On: "border-indigo-500", Off: "border-transparent" -->
                                <div    class="absolute inset-0 rounded-lg border-2 pointer-events-none"
                                        :class="showOffline ? 'border-blue-500' : 'border-transparent'"
                                        aria-hidden="true"
                                ></div>
                            </li>

                            <li     id="radiogroup-option-1" tabindex="-1" role="radio"
                                    class="mx-2 w-full md:w-4/5 group relative bg-white rounded-lg shadow-sm cursor-pointer ring-0 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    @click="showThresholdClicked"
                                >
                                <div class="rounded-lg border border-gray-300 bg-white px-6 py-2 hover:border-gray-400 flex flex-row justify-around items-center">
                                    <span class="relative flex items-center bg-white mr-2 mb-1">
                                        <label for="select-all" class="sr-only">Select all</label>
                                        <input id="select-all" :checked="showThreshold" type="checkbox" name="select-all" class="text-blue-600 focus:ring-blue-500 rounded cursor-pointer">
                                    </span>
                                    <div class="text-sm">
                                        <p class="font-medium text-gray-900 hover:text-gray-700">
                                            Threshold
                                        </p>
                                    </div>
                                </div>
                                <!-- On: "border-indigo-500", Off: "border-transparent" -->
                                <div    class="absolute inset-0 rounded-lg border-2 pointer-events-none"
                                        :class="showThreshold ? 'border-blue-500' : 'border-transparent'"
                                        aria-hidden="true"
                                ></div>
                            </li>

                            <li     v-if="hasValidationAlarms"
                                    id="radiogroup-option-1" tabindex="-1" role="radio"
                                    class="mx-2 w-full md:w-4/5 group relative bg-white rounded-lg shadow-sm cursor-pointer ring-0 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    @click="showValidationClicked"
                                >
                                <div class="rounded-lg border border-gray-300 bg-white px-6 py-2 hover:border-gray-400 flex flex-row justify-around items-center">
                                    <span class="relative flex items-center bg-white mr-2 mb-1">
                                        <label for="select-all" class="sr-only">Select all</label>
                                        <input id="select-all" :checked="showValidation" type="checkbox" name="select-all" class="text-blue-600 focus:ring-blue-500 rounded cursor-pointer">
                                    </span>
                                    <div class="text-sm">
                                        <p class="font-medium text-gray-900 hover:text-gray-700">
                                            Validation
                                        </p>
                                    </div>
                                </div>
                                <!-- On: "border-indigo-500", Off: "border-transparent" -->
                                <div    class="absolute inset-0 rounded-lg border-2 pointer-events-none"
                                        :class="showValidation ? 'border-blue-500' : 'border-transparent'"
                                        aria-hidden="true"
                                ></div>
                            </li>
                        </ul>
                    </div>
                    
                    <!-- Add Area/Zone Filter -->
                    <div    class="grid grid-cols-1 lg:grid-cols-2 w-full md:w-3/4 xl:w-1/2">
                        <!-- Zone Filter -->
                        <div    class="lg:ml-4">
                            <span   class="text-gray-700">Location</span>
                            <div    class="">
                                <!-- Swap between select and select2-group based on if an area is selected -->

                                <select2-group id="zone_id_group"
                                               v-model:model-value="$data._zoneIds"
                                               :allow-clear="true"
                                               :multiple="true"
                                               :close-on-select="true"
                                               :options="$data._namedAreasByZones"
                                               :selectable-groups="true"
                                ></select2-group>
                            </div>
                        </div>
                    </div>

                    <!-- Alarm Created Between -->
                    <div    v-if="activeHeaderFilter === 'resolved'"
                            class="flex flex-col md:flex-row items-start md:items-center space-y-1 md:space-y-0 md:space-x-4 md:my-4">
                        <span class="font-semibold text-gray-500">Time Range</span>
                        <date-time-picker   v-model="startTime"
                                            id="startPicker"
                                            placeholder="Start"

                                            :side-by-side="true"
                                            format="MM/DD/YYYY HH:mm"
                        ></date-time-picker>
                        <span class="hidden md:inline"> - </span>
                        <date-time-picker   v-model="endTime"
                                            id="endPicker"
                                            placeholder="End"

                                            :side-by-side="true"
                                            format="MM/DD/YYYY HH:mm"
                        ></date-time-picker>
                        <download-excel
                                :fetch="exportData"
                                :fields="csvFields"
                                type="csv">
                            <span v-if="exporting"
                                  class="block items-center ml-2 py-1 px-8  pl-3 border-2 border-blue-200 text-md leading-5 font-sm
                                    font-sans rounded-md text-blue-700 bg-gray-200 focus:outline-none
                                    cursor-pointer flex flex-row items-center justify-center"
                            >
                                <span :class="iconLoading"></span>
                                <span>Exporting</span>
                            </span>

                            <button v-else type="button"
                                    class="block items-center ml-2 py-1 px-8 border-2 border-blue-200 text-md leading-5 font-sm
                                    font-sans rounded-md text-blue-700 bg-white-100 hover:bg-blue-400 focus:outline-none
                                    focus:ring-blue-200 focus:border-blue-600 active:bg-blue-300 cursor-pointer"
                                    >
                                Export
                            </button>
                        </download-excel>
                    </div>
                </div>
            </div>

            <!-- Items per page -->
            <div class="flex flex-row justify-start md:justify-end">
                <!-- Mass Address Alarm, Only renders if alarms is selected -->
                <div    v-if="permissions.can('can_mass_alarms_address') && showMassAddressButton"
                        class="hidden lg:flex lg:flex-shrink lg:items-start lg:justify-center">
                    <div    class="py-2">
                        <button-blue-secondary      class="py-4 px-2 mt-2 flex flex-row items-center justify-center"
                                                    @click="showMassAddress"
                        >
                            Mass Address Selected Alarms
                        </button-blue-secondary>
                    </div>
                </div>

                <!-- Items per page -->
                <items-per-page-select  v-model:items-per-page="itemsPerPage"
                                        :current-low="pageInfo.from"
                                        :current-high="pageInfo.to"
                                        :total-results="pageInfo.total"
                ></items-per-page-select>
            </div>
        </div>

        <div class="bg-white shadow overflow-hidden sm:rounded-md border">
            <ul class="m-0">
                <!-- Header Row -->
                <li>
                    <div class="block flex justify-around text-gray-500 bg-gray-50 font-semibold grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4
                        hover:bg-gray-50 focus:outline-none focus:bg-gray-50 border">
                        <div    class="cursor-pointer inline-block grow flex items-center px-4 py-4 sm:px-6 "
                                @click="updateSort('name')">
                            <div    v-if="permissions.can('can_mass_alarms_address')"
                                    class="mb-2 mr-4"
                                    @click.stop>
                                <input      id="mass_address_all"
                                            v-model="massAddressAll"
                                            class="focus:ring-blue-500 rounded cursor-pointer"
                                            name="mass_address_all"
                                            type="checkbox"
                                            @change="setShouldShowMassAddress">
                            </div>
                            <span class="pr-2">Point Name</span>
                            <span v-show="sortBy ==='name'" :class="sortDesc ? iconSortDesc : iconSortAsc"></span>
                        </div>
                        <div class="cursor-pointer inline-block grow px-4 py-4 sm:px-6 "
                                @click="updateSort('reading')">
                            <span class="pr-2">Alarm Reading</span>
                            <span v-show="sortBy ==='reading'" :class="sortDesc ? iconSortDesc : iconSortAsc"></span>
                        </div>
                        <div class="cursor-default hidden md:inline-block grow px-4 py-4 sm:px-6 "
                                >
                            <span class="pr-2">Status</span>
                        </div>
                        <div class="cursor-pointer hidden md:inline-block grow px-4 py-4 sm:px-6 "
                                @click="updateSort('action')">
                            <span class="pr-2">Last Action</span>
                            <span v-show="sortBy ==='action'" :class="sortDesc ? iconSortDesc : iconSortAsc"></span>
                        </div>
                    </div>
                </li>

                <!-- Normal Row -->
                <li v-if="loading">
                    <div class="flex flex-row items-center justify-center py-4">
                        <span :class="iconLoading"></span>
                        <span class="text-grey-500 text-base font-medium">Loading</span>
                    </div>
                </li>
                <li v-else-if="data.length > 0" v-for="(row, rowIndex) in data"
                    :class="{'bg-gray-200' : rowIndex % 2 === 1}">
                    <!-- Configure the cols by number of columns provided by default w/ lg being max -->
                    <!-- Mobile Version -->
                    <div class="block md:hidden
                        hover:bg-gray-50"
                         @click="showRowModal(data[rowIndex])">
                        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4">
                            <!-- Cell -->
                            <!-- Alarm Status, point name, zone, & Area -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div    class="flex flex-row items-center h-full">
                                    <div>
                                <span   v-if="row.status === 'active'"
                                        :class="iconAlarmActive"
                                ></span>
                                        <span   v-else-if="row.status === 'snoozed'"
                                                :class="iconAlarmSnoozed"
                                        ></span>
                                        <span   v-else-if="row.status === 'resolved'"
                                                :class="iconAlarmResolved"
                                        ></span>
                                    </div>

                                    <div class="flex flex-col content-start ml-4">
                                <span class="font-semibold">
                                    {{ row.name }}
                                    <span class="text-gray-500 font-base hidden md:inline"> - in {{ row.zone }}</span>
                                </span>
                                        <span class="text-gray-500 hidden md:inline">{{ row.area }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Alarm Reading & Type -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold"> {{ row.reading }}</span>
                                    <span class="text-gray-500"> {{ row.time }}</span>
                                </div>
                            </div>

                            <!-- Status -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold">
                                        {{ alarmStatus(row) }}
                                    </span>
                                    <span class="text-gray-500">{{ alarmTime(row) }}</span>
                                </div>
                            </div>

                            <!-- Last Action -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                            <span class="font-semibold">
                                {{ getLastCommentActionText(row) }} by
                                {{ getLastCommentUser(row) }}
                            </span>
                                    <span class="text-gray-500">{{ row.last_comment_time }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Desktop Version -->
                    <div class="hidden md:block hover:bg-gray-50">
                        <a      :href="company.buildCompanyRoute(`alarms/${row.id}`)"
                                class="plain-link grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4">

                            <!-- Cell -->
                            <!-- Alarm Status, point name, zone, & Area -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div    class="flex flex-row items-center h-full">
                                    <!-- Link for checkbox to do mass address -->
                                    <div    v-if="permissions.can('can_mass_alarms_address')"
                                            class="hidden lg:flex h-full items-center justify-center mr-4"
                                            @click.stop>
                                        <input      :id="'mass_address_' + row.id"
                                                    v-model="shouldMassAddress[row.id]"
                                                    :name="'mass_address_' + row.id"
                                                    class="mb-2 focus:ring-blue-500 rounded cursor-pointer"
                                                    type="checkbox"
                                                    @change="setShouldShowMassAddress">
                                    </div>

                                    <!-- Icon for Alarm status -->
                                    <div>
                                        <span   v-if="row.status === 'active'"
                                                :class="iconAlarmActive"
                                        ></span>
                                        <span   v-else-if="row.status === 'snoozed'"
                                                :class="iconAlarmSnoozed"
                                        ></span>
                                        <span   v-else-if="row.status === 'resolved'"
                                                :class="iconAlarmResolved"
                                        ></span>
                                    </div>

                                    <!-- Point, Area, Zone Name. -->
                                    <div class="flex flex-col content-start ml-4">
                                        <span class="font-semibold">
                                            {{ row.name }}
                                            <span class="text-gray-500 font-base hidden md:inline"> - in {{ row.zone }}</span>
                                        </span>
                                            <span class="text-gray-500 hidden md:inline">{{ row.area }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Alarm Reading & Type -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold">{{ row.reading }}</span>
                                    <span class="text-gray-500">{{ row.time }}</span>
                                </div>
                            </div>

                            <!-- Status -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                <span class="font-semibold">
                                    {{ alarmStatus(row) }}
                                </span>
                                    <span class="text-gray-500">{{ alarmTime(row) }}</span>
                                </div>
                            </div>

                            <!-- Last Action -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold ">
                                        <span :class="getBadgeColor(row.last_comment)">
                                            {{ getLastComment(row)}}
                                        </span>
                                        by {{ getLastCommentUser(row) }}
                                    </span>
                                    <span class="text-gray-500">{{ row.last_comment_time }}</span>
                                </div>
                            </div>
                        </a>
                    </div>
                </li>
                <li v-else>
                    <div class="flex flex-row items-center justify-center py-4">
                        <span :class="iconError"></span>
                        <span v-if="activeHeaderFilter === 'active-snoozed'" class="text-grey-500 text-base font-medium">No Current Alarms</span>
                        <span v-else-if="activeHeaderFilter === 'active'" class="text-grey-500 text-base font-medium">No Active Alarms</span>
                        <span v-else class="text-grey-500 text-base font-medium">No Alarm History</span>
                    </div>
                </li>
            </ul>
            <pagination     v-if="!loading"
                            :num-pages="pageInfo.pages"
                            v-model:page="page"
                            @update:page="updatePage($event)"
            ></pagination>
        </div>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import { iconNamesMixin } from "@/mixins/IconNamesMixin";
import axios from "axios"
import { isNil } from "lodash";
import SlideOverPanel from "@/components/elements/SlideOverPanel";
import Pagination from "@/components/Pagination";
import ItemsPerPageSelect from "@/components/ItemsPerPageSelect";
import InlineTabHeader from "@/components/elements/InlineTabHeader";
import FloatingInput from "@/components/elements/FloatingInput";
import DateTimePicker from "@/components/Reports/DateTimePicker";
import JsonExcel from '@/components/JsonExcel'
import {Features} from "@/mixins/AuthorizationHelpers/Features";
import SonicuButton from "@/components/elements/buttons/SonicuButton.vue";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary.vue";
import AlarmAddress from "@/components/alarmAddress.vue";
import Select2 from "./elements/select2.vue";
import Select2Group from "./elements/select2-group.vue";

const SEARCH_DELAY = 250

export default {
    name: "AlarmsTable",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        Select2Group,
        Select2,
        AlarmAddress,
        ButtonBluePrimary,
        ButtonBlueSecondary,
        SonicuButton,
        DateTimePicker, FloatingInput, ItemsPerPageSelect, Pagination,
        SlideOverPanel, InlineTabHeader, 'download-excel' : JsonExcel
    },
    props: {
        minDate: String,
        timezone: String,
        areas: Object,
        areasByZones: Object,
    },
    data() {
        return {
            loading: false,
            data: [],
            modalAlarm: null,
            showModal: false,
            itemsPerPage: '10',
            search: '',
            pageInfo: {
                pages: 1,
                from: 1,
                to: 1,
                total: 1,
            },
            page: 1,
            sortBy: 'reading',
            sortDesc: true,
            loadFunction: null,
            startTime: null,
            endTime: null,
            _zoneIds: null,
            _zoneIdByArea: null,

            // Inline Tab
            filterHeaders: [
                {
                    text: 'Alarms',
                    event: 'active-snoozed',
                },
                {
                    text: 'Active',
                    event: 'active',
                },
                {
                    text: 'Resolved',
                    event: 'resolved',
                },
            ],
            showActive: true,
            showSnoozed: true,
            showResolved: false,
            showOffline: true,
            showThreshold: true,
            showValidation: true,
            activeHeaderFilter: 'active-snoozed',
            csvFields: {
                'Area' : 'area',
                'Zone' : 'zone',
                'Device Name' : 'name',
                'Serial Number' : 'serial',
                'Alarm Time' : 'time_export_readable',
                'Resolved By' : 'resolved_by',
                'Resolved Time' : 'silenced_at_readable',
                'Alarm Reading' : 'reading',
                'Acknowledge Comment' : 'silence_comment',
            },
            exporting: false,
            showMassAddressSideOver: false,
            savingMassAddress: false,
            massAddressAll: false,
            showMassAddressButton: false,
            shouldMassAddress: {},
            _namedAreasByZones: {},
        }
    },
    created() {
        this.loadFunction = _.debounce(() => {
            this.loading = true
            let startTime = ! this.isInvalidDate(this.startTime)
                ? this.startTime.toISOString()
                : null
            let endTime = ! this.isInvalidDate(this.endTime)
                ? this.endTime.toISOString()
                : null

            let params = {
                page: this.page,
                perPage: this.itemsPerPage,
                search: this.search,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc,
                showActive: this.showActive,
                showSnoozed: this.showSnoozed,
                showResolved: this.showResolved,
                showOffline: this.showOffline,
                zoneIds: this.$data._zoneIds,
                showThreshold: this.showThreshold,
                showValidation: this.showValidation,
                startTime: startTime,
                endTime: endTime,
            }
            this.storeUrl(params)
            this.massAddressAll = false
            axios.get(this.company.buildCompanyApiInternalRoute('alarms/'), {
                params: params
            }).then(response => {
                this.loading = false
                this.data = response.data.data !== undefined ? response.data.data : []
                this.shouldMassAddress = {}
                _.each(this.data, (row, index) => {
                    this.shouldMassAddress[row.id] = false
                })
                if (response.data.meta) {
                    this.pageInfo.pages = response.data.meta.last_page
                    this.pageInfo.from = response.data.meta.from
                    this.pageInfo.to = response.data.meta.to
                    this.pageInfo.total = response.data.meta.total
                }
                else {
                    this.pageInfo = {
                        pages: 1,
                        from: 0,
                        to: 0,
                        total: 0,
                    }
                }
                if (this.page > this.pageInfo.pages) {
                    this.page = this.pageInfo.pages
                    this.loadData()
                }

                this.setupNamedAreasByZones()

            }).catch(error => {
                this.loading = false
                this.data = []
            })
        }, SEARCH_DELAY)
        
        // Change the start date to created date of the company
        this.startTime = new Date(this.minDate)
    },
    mounted() {
        this.readUrl()
        window.addEventListener('popstate', this.handleBackButton)
        this.loadData()
    },
    methods: {
        loadData() {
            // this.data = []
            this.loadFunction()
        },
        // This is cool, makes sure the request is inline, only issue is it could hold up the page for long requests
        // TODO: Make it so if the export has more than some number N alarms, we should instead flash a notification
        //      that it is a large request & we will email them the csv when completed.
        async exportData() {
            this.exporting = true
            let params = {
                page: 1,
                perPage: this.pageInfo.total.toString(),
                search: this.search,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc,
                showActive: this.showActive,
                showSnoozed: this.showSnoozed,
                showResolved: this.showResolved,
                startTime: this.startTime !== null ? `${this.startTime.toISOString()}` : null,
                endTime: this.endTime !== null ? `${this.endTime.toISOString()}` : null,
            }

            let newData = []
            const response = await axios.get(this.company.buildCompanyApiInternalRoute('alarms/'), {
                params: params
            })

            newData = response.data.data !== undefined ? response.data.data : []
            this.exporting = false

            return newData
        },
        showMassAddress() {
            this.showMassAddressSideOver = true
        },
        massAddress(action, event) {
            // Set up the parameters, filtering down to the list of alarms selected
            let params = {
                alarmsToAddress: this.data.filter(alarm => this.shouldMassAddress[alarm.id]),
                action: action,
            }
            params = _.merge(params, event)
            // Convert the alarms to address to a list of alarm ids.
            _.each(params.alarmsToAddress, (alarm, index) => {
                params.alarmsToAddress[index] = alarm.id
            })

            // Submit to a mass address alarm controller.
            axios.patch(this.company.buildCompanyApiInternalRoute('alarms/mass-address'), params)
                .then(response => {
                    let data = response.data
                    console.log(response)
                })
                .catch(error => {
                    console.log(error)
                })

            // Close the slide over and refresh the table
            this.showMassAddressSideOver = false
            this.loadData()
        },
        setShouldShowMassAddress() {
            let oneOrMoreSelected = false
            _.each(this.shouldMassAddress, (shouldAlarm, index) => {
                if (this.shouldMassAddress[index]) {
                    oneOrMoreSelected = true
                }
            })

            this.showMassAddressButton = oneOrMoreSelected
        },
        updatePage(page) {
            this.page = page
            this.loadData()
        },
        alarmStatus(alarm) {
            if (alarm.silenced_at){
                return 'Resolved'
            }
            else if (alarm.snooze_until) {

                return this.isSilenced(alarm) ? 'Silenced' : 'Snoozed'
            }
            else {
                return 'Active'
            }
        },
        isSilenced(alarm) {
            if (!alarm.snooze_until) {
                return false
            }

            let silencedTime = alarm.snooze_until.split(' years')
            let silenced = silencedTime.length > 1
            if (silencedTime.length > 1) {
                silencedTime = Number(silencedTime[0])
            }
            return silencedTime >= 2
        },
        alarmTime(alarm) {
            if (alarm.silenced_at) {
                return `${alarm.silenced_at_diff}`
            }
            else if (alarm.snooze_until) {
                return this.isSilenced(alarm) ? `` : `For ${alarm.snooze_until_diff}`
            }
            else {
                return `For ${alarm.time_diff}`
            }
        },
        getLastComment(alarm) {
            if (!alarm.last_comment.user_id) {
                return 'Generated'
            }
            if (this.isSilenced(alarm)) {
                return 'Silenced'
            }
            return alarm.last_comment.comment_type
        },
        getLastCommentUser(alarm) {
            let comment = alarm.last_comment
            if(typeof comment.user_name === 'string' || comment.user_name instanceof String) {
                return comment.user_name
            }
            if (!isNil(comment.user)) {
                return comment.user.display_name
            }
            else if (!isNil(comment.user_name)) {
                return comment.user_name
            }
            return comment.data.user
        },
        getLastCommentActionText(alarm){
            let comment = alarm.last_comment
            if(comment.user_id === null){
                return 'Generated';
            } else {
                return comment.comment_type;
            }
        },
        showRowModal(alarm) {
            this.modalAlarm = alarm
            this.showModal = true
        },
        updateSort(by) {
            if (this.sortBy === by) {
                this.sortBy = this.sortDesc ? '' : this.sortBy
                this.sortDesc = !this.sortDesc
            }
            else {
                this.sortBy = by
                this.sortDesc = false
            }
            this.loadData()
        },
        updateCurrentFilter(currentFilter) {
            this.activeHeaderFilter = currentFilter
            this.page = 1
            this.showActive = currentFilter === 'active' ||
                currentFilter === 'active-snoozed'
            this.showSnoozed = currentFilter === 'active-snoozed'
            this.showResolved = currentFilter === 'resolved'
            this.loadData()
        },
        showOfflineClicked() {
            this.showOffline = !this.showOffline
            this.loadData()
        },
        showThresholdClicked() {
            this.showThreshold = !this.showThreshold
            this.loadData()
        },
        showValidationClicked() {
            this.showValidation = !this.showValidation
            this.loadData()
        },
        getBadgeColor(comment){
            if(comment.badge_color === 'gray'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-gray-100 text-gray-800';
            }
            if(comment.badge_color === 'red'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-red-100 text-red-800';
            }
            if(comment.badge_color === 'green'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-green-100 text-green-800';
            }
            if(comment.badge_color === 'yellow'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-yellow-100 text-yellow-800';
            }
            if(comment.badge_color === 'blue'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-blue-100 text-blue-800';
            }
        },
        setupNamedAreasByZones() {
            // This is for the purposes of the options list of the select2-group.
            //   It is using the areasByZones list as its options, but that is keyed by areas for obvious reasons.
            //   We will however locally make a copy that is keyed by the area name for user purposes
            let namedAreasByZones = {}
            _.each(this.areasByZones, (zones, area) => {
                namedAreasByZones[this.areas[area]] = zones
            })

            this._namedAreasByZones = namedAreasByZones
        },
        handleBackButton() {
            this.readUrl()
            this.loadData()
        },
        readUrl() {
            let url = window.location.href.split('?')
            let data = []
            if (url.length <= 1) {
                return data
            }

            url = url[1].split('&')
            url.forEach(param => {
                param = param.split('=')
                data[param[0]] = param[1]
            })

            this.page = !isNil(data['page']) ? Number(data['page']) : this.page
            this.itemsPerPage = !isNil(data['perPage']) ? data['perPage'] : this.itemsPerPage
            this.search = !isNil(data['search']) ? data['search'] : this.search
            this.showActive = !isNil(data['showActive']) ? data['showActive'] === 'true' : this.showActive
            this.showResolved = !isNil(data['showResolved']) ? data['showResolved'] === 'true' : this.showResolved
            this.showSnoozed = !isNil(data['showSnoozed']) ? data['showSnoozed'] === 'true' : this.showSnoozed
            this.showOffline = !isNil(data['showOffline']) ? data['showOffline'] === 'true' : this.showOffline
            this.showThreshold = !isNil(data['showThreshold']) ? data['showThreshold'] === 'true' : this.showThreshold
            this.showValidation = !isNil(data['showValidation']) ? data['showValidation'] === 'true' : this.showValidation
            this.sortBy = data['sortBy']
            this.sortDesc = data['sortDesc']
            this.startTime = 'startTime' in data && data['startTime'] !== "null" ? new Date(data['startTime']) : null
            this.endTime = 'startTime' in data && data['endTime'] !== "null" ? new Date(data['endTime']) : null
            this.activeHeaderFilter = this.showResolved === true ? 'resolved' :
                (this.showSnoozed !== true && this.showActive === true ? 'active' : 'active-snoozed')

            // Post Store Processing
            this.search = this.search.replaceAll('%20', ' ')
        },
        storeUrl(data) {
            let url = ''

            Object.keys(data).forEach(key => {
                url += `${key}=${data[key]}&`
            })
            window.history.pushState(window.location.hostname, "Table", this.company.buildCompanyRoute('alarms?') + url)
        },
        isInvalidDate(date) {
            return ! _.isDate(date) || isNaN(date.getTime())
        },
      isNil,
    },
    watch: {
        itemsPerPage() {
            this.loadData()
        },
        search() {
            this.loadData()
        },
        startTime() {
            this.loadData()
        },
        endTime() {
            this.loadData()
        },
        _zoneIds(val, old) {
            if (val === old) {
                return
            }

            this.$data._zoneIdByArea = null
            this.loadData()
        },
        _zoneIdByArea(val, old) {
            if (val === old) {
                return
            }

            this.$data._zoneIds = null
            this.loadData()
        },
        massAddressAll() {
            let value = this.massAddressAll

            // For each alarm, mark address alarm checkbox to match
            _.each(this.shouldMassAddress, (val, key) => {
                this.shouldMassAddress[key] = value
            })
        }
    },
    computed: {
        hasValidationAlarms(){
          return this.featureLevel.hasFeature(Features.VALIDATION_ALARM);
        },
    },
    unmounted() {
        window.removeEventListener('popstate', this.handleBackButton)
    }
}
</script>

<style scoped>

</style>
